import { Box, makeStyles } from '@material-ui/core';
import { SelectableProfile, UserType } from '@nemuru-eng/js-api-client';

import { Font } from '@/components/shared/components/Font';
import { IconWithCircle } from '@/components/shared/components/IconWithCircle';
import { ICON_NAMES } from '@/config/config';
import { useCustomTheme } from '@/hooks/useCustomTheme';

interface ProfileAvatarProps {
  profile: SelectableProfile;
  /** Diameter in px: 40 in the chip and the Account rows, 32 in the switcher menu (spec §3.8). */
  size?: 40 | 32;
}

const PROFILE_TYPE_ICON_NAMES: Partial<Record<UserType, string>> = {
  [UserType.SUPER_ADMIN]: ICON_NAMES.SUPER_ADMIN,
  [UserType.ADMIN]: ICON_NAMES.ADMIN,
  [UserType.LENDER]: ICON_NAMES.LENDER,
  [UserType.MERCHANT]: ICON_NAMES.MERCHANT,
  [UserType.CHANNEL]: ICON_NAMES.CHANNEL,
  [UserType.AGENT]: ICON_NAMES.AGENT,
  [UserType.MERCHANT_GROUP]: ICON_NAMES.PARTNER,
  [UserType.PARTNER]: ICON_NAMES.PARTNER,
  [UserType.THIRD_PARTY_VALIDATION]: ICON_NAMES.THIRD_PARTY_VALIDATION,
  [UserType.CPC_BACK_OFFICE]: ICON_NAMES.CPC_BACK_OFFICE,
};

const useStyles = makeStyles({
  image: { width: '100%', height: '100%', objectFit: 'contain', display: 'block' },
});

const initialsOf = (name: string): string =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('');

/** Round avatar of a profile: its image, else the initials of its display name, else the icon of its type. */
export const ProfileAvatar = ({ profile, size = 40 }: ProfileAvatarProps) => {
  const { palette, customBorderRadius } = useCustomTheme();
  const classes = useStyles();

  if (profile.imageUrl) {
    return (
      <Box
        width={size}
        height={size}
        borderRadius={customBorderRadius.chip}
        overflow="hidden"
        flexShrink={0}
        bgcolor={palette.grey[100]}
      >
        <img className={classes.image} src={profile.imageUrl} alt={profile.displayName ?? profile.username} />
      </Box>
    );
  }
  if (profile.displayName) {
    return (
      <Box
        width={size}
        height={size}
        borderRadius={customBorderRadius.chip}
        bgcolor={palette.primary.main}
        color={palette.primary.contrastText}
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
      >
        <Font variant={size === 40 ? 'subtitle2' : 'caption'} color="inherit">
          <strong>{initialsOf(profile.displayName)}</strong>
        </Font>
      </Box>
    );
  }
  return (
    <IconWithCircle
      width={size}
      height={size}
      bgcolor={palette.grey[200]}
      color={palette.text.secondary}
      flexShrink={0}
      iconProps={{ style: { fontSize: size === 40 ? 22 : 18 } }}
    >
      {PROFILE_TYPE_ICON_NAMES[profile.userType] ?? ICON_NAMES.AGENT}
    </IconWithCircle>
  );
};
